/**
 * useGameSync Hook
 *
 * Observes GameContext state changes and syncs to Supabase in the background.
 * All Supabase operations are fire-and-forget — failures are logged silently.
 * The game continues working via localStorage regardless of Supabase status.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import type { GameState } from '../types';
import { HINT_COST } from '../utils/constants';
import {
  findExistingSession,
  createSession,
  saveSessionState,
  logGameEvent,
  updateUserStats,
  boardStateToGameState,
} from '../lib/gameSync';
import type { GameEventType } from '../lib/gameSync';

interface UseGameSyncOptions {
  gameState: GameState;
  puzzleLoaded: boolean;
  hadLocalState: boolean;
  onRemoteStateLoaded?: (remoteState: Partial<GameState>) => void;
}

/**
 * Extract only the fields that should trigger Supabase syncs.
 * UI-only fields (dialog state, animation) are excluded.
 */
function getPersistedFields(state: GameState) {
  return {
    credits: state.credits,
    revealedClues: state.revealedClues,
    solvedColumns: state.solvedColumns,
    columnAnswers: state.columnAnswers,
    guessAttempts: state.guessAttempts,
    incorrectGuesses: state.incorrectGuesses,
    finalAnswerSubmitted: state.finalAnswerSubmitted,
    gameWon: state.gameWon,
    finalScore: state.finalScore,
    endTime: state.endTime,
  };
}

function hasPersistedFieldsChanged(prev: GameState | null, next: GameState): boolean {
  if (!prev) return true;
  return JSON.stringify(getPersistedFields(prev)) !== JSON.stringify(getPersistedFields(next));
}

/**
 * Detect which game events occurred by diffing previous and current state.
 */
function detectEvents(
  prev: GameState | null,
  next: GameState
): Array<{ type: GameEventType; data: Record<string, unknown> }> {
  if (!prev) return [];
  const events: Array<{ type: GameEventType; data: Record<string, unknown> }> = [];

  const newlySolved = next.solvedColumns.filter(c => !prev.solvedColumns.includes(c));

  if (next.credits === prev.credits - HINT_COST && newlySolved.length === 0) {
    for (let col = 0; col < next.revealedClues.length; col++) {
      for (let row = 0; row < next.revealedClues[col].length; row++) {
        if (next.revealedClues[col][row] && !prev.revealedClues[col][row]) {
          events.push({
            type: 'hint_revealed',
            data: { column: col, row, credits_after: next.credits },
          });
        }
      }
    }
  }

  for (const col of newlySolved) {
    events.push({
      type: 'column_guess_correct',
      data: {
        column: col,
        answer: next.columnAnswers[col],
        credits_after: next.credits,
      },
    });
  }

  for (const colStr of Object.keys(next.guessAttempts)) {
    const col = Number(colStr);
    const prevAttempts = prev.guessAttempts[col] || 0;
    const nextAttempts = next.guessAttempts[col] || 0;
    if (nextAttempts > prevAttempts && !newlySolved.includes(col)) {
      const wrongGuesses = next.incorrectGuesses[col] || [];
      events.push({
        type: 'column_guess_incorrect',
        data: {
          column: col,
          guess: wrongGuesses[wrongGuesses.length - 1],
          credits_after: next.credits,
        },
      });
    }
  }

  if (next.finalAnswerSubmitted && !prev.finalAnswerSubmitted) {
    if (next.finalAnswerSubmitted === '__CREDITS_EXHAUSTED__') {
      events.push({ type: 'credits_exhausted', data: { credits_remaining: 0 } });
    } else {
      events.push({
        type: 'final_answer_submitted',
        data: { answer: next.finalAnswerSubmitted },
      });
    }
  }

  if (next.gameWon === true && prev.gameWon === null) {
    events.push({
      type: 'game_won',
      data: { score: next.finalScore, credits_remaining: next.credits },
    });
  }
  if (next.gameWon === false && prev.gameWon === null) {
    events.push({
      type: 'game_lost',
      data: { credits_remaining: next.credits },
    });
  }

  return events;
}

export function useGameSync({
  gameState,
  puzzleLoaded,
  hadLocalState,
  onRemoteStateLoaded,
}: UseGameSyncOptions) {
  const { user } = useAuth();

  // Refs to avoid stale closures
  const gameStateRef = useRef(gameState);
  const hadLocalStateRef = useRef(hadLocalState);
  const onRemoteStateLoadedRef = useRef(onRemoteStateLoaded);
  gameStateRef.current = gameState;
  hadLocalStateRef.current = hadLocalState;
  onRemoteStateLoadedRef.current = onRemoteStateLoaded;

  const sessionIdRef = useRef<string | null>(null);
  const prevStateRef = useRef<GameState | null>(null);
  const initializedRef = useRef(false);
  const initializingRef = useRef(false);

  // Promise chain to serialize all Supabase calls (prevents connection exhaustion)
  const syncChain = useRef(Promise.resolve());

  // Phase 1: Session initialization (runs once when user + puzzle are ready)
  useEffect(() => {
    if (!user || !puzzleLoaded || initializedRef.current || initializingRef.current) {
      return;
    }

    initializingRef.current = true;
    const currentState = gameStateRef.current;
    const userId = user.id;

    (async () => {
      try {
        const puzzleId = currentState.puzzleUuid ?? currentState.date;
        const existing = await findExistingSession(userId, puzzleId);

        if (existing) {
          sessionIdRef.current = existing.session.id;

          if (!hadLocalStateRef.current && existing.boardState && onRemoteStateLoadedRef.current) {
            const remoteState = boardStateToGameState(existing.session, existing.boardState);
            onRemoteStateLoadedRef.current(remoteState);
          } else {
            await saveSessionState(existing.session.id, currentState);
          }
        } else {
          const sessionId = await createSession(userId, puzzleId, currentState.startTime);
          if (sessionId) {
            sessionIdRef.current = sessionId;
            await logGameEvent(sessionId, 'game_started', {
              date: currentState.date,
              credits: currentState.credits,
            });
          }
        }

        prevStateRef.current = currentState;
        initializedRef.current = true;
        console.log('[GameSync] Initialized. Session:', sessionIdRef.current);
      } catch (err) {
        console.error('[GameSync] Initialization failed:', err);
      } finally {
        initializingRef.current = false;
      }
    })();
  }, [user, puzzleLoaded]);

  // Phase 2: Sync on gameState changes — chained to serialize Supabase calls
  useEffect(() => {
    if (!initializedRef.current || !sessionIdRef.current || !user) {
      return;
    }

    if (!hasPersistedFieldsChanged(prevStateRef.current, gameState)) {
      return;
    }

    const prevState = prevStateRef.current;
    const currentState = gameState;
    const sessionId = sessionIdRef.current;
    const userId = user.id;

    // Update prevState synchronously so next render sees the latest
    prevStateRef.current = currentState;

    // Chain onto previous sync to prevent concurrent requests
    syncChain.current = syncChain.current.then(async () => {
      await saveSessionState(sessionId, currentState);

      const events = detectEvents(prevState, currentState);
      for (const event of events) {
        await logGameEvent(sessionId, event.type, event.data);
      }

      if (currentState.gameWon !== null && prevState?.gameWon === null) {
        await updateUserStats(userId, currentState.gameWon === true, currentState.finalScore || 0);
      }
    }).catch(err => {
      console.error('[GameSync] Sync failed:', err);
    });
  }, [gameState, user]);
}
